import { ProductName, productNames } from "@/common/common";
import { TMessage } from "@/i18n/tt";

export type EventLog = {
  id: string;
  actor: Actor;
  eventType: EventType;
  eventData: string;
  timestamp: string;
};

export type Actor = {
  id: string;
  name: string;
  email?: string;
};

// noinspection JSUnusedGlobalSymbols
export enum EventType {
  AcceptedCompanyTerms = "AcceptedCompanyTerms",
  AcceptedInvitationByAUser = "AcceptedInvitationByAUser",
  AcceptedUserTerms = "AcceptedUserTerms",
  ActivatedIncorporation = "ActivatedIncorporation",
  AddToEnterpriseAccount = "AddToEnterpriseAccount",
  ApiCallEvent = "ApiCallEvent",
  BeneficialOwnersStatementSubmitted = "BeneficialOwnersStatementSubmitted",
  BRStatusUpdateEmail = "BRStatusUpdateEmail",
  BRSignerReminderEmail = "BRSignerReminderEmail",
  BRSentForSigning = "BRSentForSigning",
  BRProcessingInitiated = "BRProcessingInitiated",
  CancelledSignatureRequestEvent = "CancelledSignatureRequestEvent",
  ChangedNominalValue = "ChangedNominalValue",
  ChangedTransactionDate = "ChangedTransactionDate",
  CompanyMovedToPortfolio = "CompanyMovedToPortfolio",
  CompanyRemovedFromPortfolio = "CompanyRemovedFromPortfolio",
  CreatedPortfolio = "CreatedPortfolio",
  CreatedUserApiKey = "CreatedUserApiKey",
  CustomRoleDelete = "CustomRoleDelete",
  CustomRoleInvite = "CustomRoleInvite",
  CustomRoleNew = "CustomRoleNew",
  CustomRoleRevoke = "CustomRoleRevoke",
  CustomRoleUpdate = "CustomRoleUpdate",
  DeletedAutoimport = "DeletedAutoimport",
  DeletedComment = "DeletedComment",
  DeletedLastTransactionLog = "DeletedLastTransactionLog",
  DeletedShareholderRegister = "DeletedShareholderRegister",
  DeletedTransactionLog = "DeletedTransactionLog",
  DeregisteredCompany = "DeregisteredCompany",
  DuplicatedProject = "DuplicatedProject",
  GeneralMeetingStatusUpdate = "GeneralMeetingStatusUpdate",
  GivenAdminAccessToUser = "GivenAdminAccessToUser",
  GrantedAdminAccess = "GrantedAdminAccess",
  MigratedBoardMeetingsFoldersToRootFolder = "MigratedBoardMeetingsFoldersToRootFolder",
  MigratedProject = "MigratedProject",
  MigratePriceEvent = "MigratePriceEvent",
  ModifiedPermissions = "ModifiedPermissions",
  NotifiedShareholders = "NotifiedShareholders",
  OrderLiveGeneralMeeting = "OrderLiveGeneralMeeting",
  PortfolioDeleted = "PortfolioDeleted",
  PortfolioUpdated = "PortfolioUpdated",
  RegisteredShareholderCommitment = "RegisteredShareholderCommitment",
  RegisteredShareTransferInShareholderRegister = "RegisteredShareTransferInShareholderRegister",
  ReplayedTransactions = "ReplayedTransactions",
  RerunSealing = "RerunSealing",
  ResendDocumentForSigning = "ResendDocumentForSigning",
  RevokedAdminAccess = "RevokedAdminAccess",
  RoleMappingAdded = "RoleMappingAdded",
  RoleMappingRemoved = "RoleMappingRemoved",
  SearchedBeneficialOwners = "SearchedBeneficialOwners",
  SentMeetingEmail = "SentMeetingEmail",
  SentMessageFromMessaging = "SentMessageFromMessaging",
  SentNoticeOfBoardMeeting = "SentNoticeOfBoardMeeting",
  SentNoticeOfGeneralMeeting = "SentNoticeOfGeneralMeeting",
  SharepointSyncEvent = "SharepointSyncEvent",
  SignedBoardMinutes = "SignedBoardMinutes",
  SignedDocumentSealed = "SignedDocumentSealed",
  SignedGeneralMeeting = "SignedGeneralMeeting",
  SignedSubscriptionForm = "SignedSubscriptionForm",
  SubmittedDecisionToBR = "SubmittedDecisionToBR",
  SubmittedShareholderRegisterStatement = "SubmittedShareholderRegisterStatement",
  SubscribedWithNewInvoice = "SubscribedWithNewInvoice",
  UpdatedDecision = "UpdatedDecision",
  UpdatedEmail = "UpdatedEmail",
  UpdatedProject = "UpdatedProject",
  UsedAIAssistant = "UsedAIAssistant",
  VerifiedUserEmail = "VerifiedUserEmail",
}

export type EventLogDescription = {
  timestamp: string;
  description: TMessage;
};

export function toEventLogDescription(eventLog: EventLog): EventLogDescription | undefined {
  switch (eventLog.eventType) {
    case EventType.SubmittedDecisionToBR:
      return { timestamp: eventLog.timestamp, description: { key: "event.SubmittedDecisionToBR" } };
    case EventType.BRSentForSigning:
      return { timestamp: eventLog.timestamp, description: { key: "event.BRSentForSigning" } };
    case EventType.BRSignerReminderEmail:
      return {
        timestamp: eventLog.timestamp,
        description: {
          key: "event.BRSignerReminderEmail",
          params: {
            signers: {
              type: "list",
              value: extractBRSignerReminders(eventLog).map((a) => a.name ?? a.email),
            },
          },
        },
      };
    case EventType.BRProcessingInitiated:
      return { timestamp: eventLog.timestamp, description: { key: "event.BRProcessingInitiated" } };
    default:
      return undefined;
  }
}

export type AltinnSigner = {
  name?: string;
  email: string;
};

export const extractBRSignerReminders = (event: EventLog): AltinnSigner[] =>
  JSON.parse(event.eventData).signers;

export type ActivatedIncorporationData = {
  newOrgNo: string;
  companyName: string;
  adminNames: string[];
  productName: ProductName | undefined;
  enterpriseAccountName: string | undefined;
};

const toProductName = (value: unknown): ProductName | undefined =>
  productNames.find((product) => product === value);

export const extractActivatedIncorporation = (
  event: EventLog,
): ActivatedIncorporationData | undefined => {
  if (event.eventType !== EventType.ActivatedIncorporation) return undefined;
  try {
    const data = JSON.parse(event.eventData);
    return typeof data.newOrgNo === "string" && typeof data.companyName === "string"
      ? {
          newOrgNo: data.newOrgNo,
          companyName: data.companyName,
          adminNames: Array.isArray(data.adminNames)
            ? data.adminNames.filter((name: unknown) => typeof name === "string")
            : [],
          productName: toProductName(data.productName),
          enterpriseAccountName:
            typeof data.enterpriseAccountName === "string" ? data.enterpriseAccountName : undefined,
        }
      : undefined;
  } catch {
    return undefined;
  }
};

export type RegisteredShareTransferData = {
  transferorName: string;
  acquirerName: string;
  numberOfShares: number;
  pricePerShare?: number;
};

export const extractRegisteredShareTransfer = (
  event: EventLog,
): RegisteredShareTransferData | undefined => {
  if (event.eventType !== EventType.RegisteredShareTransferInShareholderRegister) return undefined;
  try {
    const data = JSON.parse(event.eventData);
    return typeof data.transferorName === "string" &&
      typeof data.acquirerName === "string" &&
      typeof data.numberOfShares === "number"
      ? {
          transferorName: data.transferorName,
          acquirerName: data.acquirerName,
          numberOfShares: data.numberOfShares,
          pricePerShare: typeof data.pricePerShare === "number" ? data.pricePerShare : undefined,
        }
      : undefined;
  } catch {
    return undefined;
  }
};

export const extractBRStatusEmailBody = (event: EventLog): string =>
  JSON.parse(event.eventData).updateText;

export const extractIsRegistration = (event: EventLog): boolean => {
  const data = JSON.parse(event.eventData);
  if (event.eventType === "SubmittedDecisionToBR") {
    return data.decision?.isRegistration ?? false;
  }
  return data.isRegistration ?? false;
};
